import { LazyQueryHookOptions, gql } from "@apollo/client"
import { UseQueryOptions } from "@oysterhr/core"
import { CalculationQueryFields } from "~/shared/components/CostCalculator/types"
import { SalaryCalculation } from "@oysterhr/schema"
import {
  useLazyPublicCostCalculatorQuery,
  usePublicCostCalculatorQuery,
} from "./usePublicCostCalculatorQuery"

export const TaxDataFragment = gql`
  fragment TaxData on SalaryTax {
    total
    contributions {
      name
      group
      amount
      metadata {
        benefitEssential
        benefitBestInClass
        benefitCompetitive
        benefitHealthAddon
      }
    }
  }
`

export const FeeDataFragment = gql`
  fragment FeeData on SalaryFee {
    value
    rate
    currencyCode
  }
`

const SalaryCalculationFragment = gql`
  ${TaxDataFragment}
  ${FeeDataFragment}
  fragment CommonSalaryData on SalaryCalculation {
    country {
      code
      name
      notes {
        name
        notes
      }
      liability {
        tier
        link
      }
    }
    vatOptions {
      requiresSalesPercentageToCountry
      requiresSalesToCountry
      requiresWorksInCountryOfResidence
    }
    vatParameters {
      salesPercentageToCountry
      salesToCountry
      worksInCountryOfResidence
    }
    taxes {
      employer {
        ...TaxData
      }
      employee {
        ...TaxData
      }
    }
    annualGrossSalary
    stateOrProvince
    selectedSalary
    salaryFrequency
    currency {
      code
      name
    }
    fees {
      oyster {
        feeInEngagementSalaryCurrency {
          ...FeeData
        }
        feeInCompanyPaymentCurrency {
          ...FeeData
        }
        companysPlanPrice {
          ...FeeData
        }
      }
      vat {
        ...FeeData
      }
    }
    totals {
      netSalary
      employerCosts
    }
  }
`

export const BULK_SALARY_CALCULATION_QUERY = gql`
  ${SalaryCalculationFragment}

  query BulkSalaryCalculations($calculationQueries: [SalaryQueryInput!]!) {
    bulkSalaryCalculations(calculationQueries: $calculationQueries) {
      ...CommonSalaryData
    }
  }
`

export const SALARY_CALCULATION_QUERY = gql`
  ${TaxDataFragment}
  ${SalaryCalculationFragment}

  fragment zoneInfo on TimeZone {
    identifier
    utcOffset
  }

  fragment Discount on SalaryFee {
    discount
    discountRate
    withoutDiscount
  }

  query SalaryCalculation(
    $countryCode: [String!]
    $currencyCode: String!
    $stateOrProvince: String
    $annualGrossSalary: Float!
    $targetTz: String
    $maxTzOffsetMinutes: Int
  ) {
    salaryCalculation(
      countryCode: $countryCode
      currencyCode: $currencyCode
      stateOrProvince: $stateOrProvince
      annualGrossSalary: $annualGrossSalary
      targetTz: $targetTz
      maxTzOffsetMinutes: $maxTzOffsetMinutes
    ) {
      ...CommonSalaryData
      country {
        employmentCostRange
        tzinfo {
          zones {
            ...zoneInfo
          }
          maxUtcOffset
          minUtcOffset
        }
      }

      fees {
        oyster {
          feeInEngagementSalaryCurrency {
            ...Discount
            value
          }
          feeInCompanyPaymentCurrency {
            ...Discount
            value
            discount
            discountRate
            withoutDiscount
          }
        }
        vat {
          value
          rate
        }
      }
    }
  }
`

export const useSalaryCalculation = (
  options?: UseQueryOptions<
    { salaryCalculation: SalaryCalculation[] },
    {
      countryCode: string[]
      currencyCode: string
      stateOrProvince?: string
      annualGrossSalary: number
      targetTz?: string
      maxTzOffsetMinutes?: number
    }
  >,
) => {
  return usePublicCostCalculatorQuery(SALARY_CALCULATION_QUERY, options)
}

export const useBulkSalaryCalculation = (
  options?: UseQueryOptions<
    { bulkSalaryCalculations: SalaryCalculation[] },
    {
      calculationQueries: CalculationQueryFields[]
    }
  >,
) => {
  return usePublicCostCalculatorQuery(BULK_SALARY_CALCULATION_QUERY, options)
}

export const useLazyBulkSalaryCalculation = (
  options?: LazyQueryHookOptions<
    { bulkSalaryCalculations: SalaryCalculation[] },
    {
      calculationQueries: CalculationQueryFields[]
    }
  >,
) => {
  return useLazyPublicCostCalculatorQuery(BULK_SALARY_CALCULATION_QUERY, options)
}
